import React, { useMemo, useState } from "react";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { ScaledIframe } from "newui/components/ScaledIframe";
import { PreviewPageCard } from "newui/components/PreviewPageCard";
import { Button } from "newui/components/Button";
import { ToggleButtonGroup } from "newui/components/ui/ToggleButtonGroup";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel";
import type { SelectOption } from "newui/components/ui/InputWithLabel";
import { useResolvedSchema } from "newui/features/Settings/PdfSettings/hooks/useDesignSchema";
import type { ResolvedDesignSchema as DesignSchema } from "newui/features/Settings/PdfSettings/types/DesignSchema.type";
import { useCurrentCompanyId } from "hooks/auth";
import { useReportPreview } from "../../../../hooks/useReportPreview";
import { useExportReport } from "../../../../hooks/useExportReport";
import type {
	ReportExportPayload,
	ReportType,
} from "../../../../api/evaluation-report-api";
import type { AwardRow } from "../../../../types/award.type";

const A4_PORTRAIT = { width: 794, height: 1123 };
const A4_LANDSCAPE = { width: 1123, height: 794 };
const PREVIEW_WIDTH = 560;

const REPORT_TYPE_OPTIONS = [
	{ label: "Awarded Supplier Report", value: "awarded" },
	{ label: "All Supplier Report", value: "all" },
];

interface ReportPanelProps {
	/** Suppliers with `award_status === 'awarded'` (drives the gate + selector). */
	awardedSuppliers: AwardRow[];
	/**
	 * Optional live brand override. The full brand editor is reused from
	 * PdfSettings (deferred); when supplied the preview re-fetches (debounced).
	 */
	designSchema?: DesignSchema;
	evalId?: number;
	rfxId?: number;
}

export const ReportPanel = ({
	rfxId,
	evalId,
	awardedSuppliers,
	designSchema,
}: ReportPanelProps) => {
	const [reportType, setReportType] = useState<ReportType>("awarded");
	const [selectedInviteId, setSelectedInviteId] = useState<number | undefined>(
		awardedSuppliers[0]?.rfx_invite_id
	);

	// Seed the live preview from the company's resolved brand schema (lead
	// ruling). An explicit `designSchema` prop (from a PdfSettings surface)
	// overrides it; the preview hook debounces re-fetches on schema change.
	const companyId = useCurrentCompanyId();
	const { data: resolvedSchema } = useResolvedSchema(
		companyId ?? undefined,
		"rfx_evaluation_report"
	);
	const effectiveSchema = designSchema ?? resolvedSchema;

	const hasAwards = awardedSuppliers.length > 0;

	const supplierOptions: SelectOption[] = useMemo(
		() =>
			awardedSuppliers.map((s) => ({
				label: s.supplier.name,
				value: s.rfx_invite_id,
			})),
		[awardedSuppliers]
	);

	const params = useMemo(
		() => ({
			type: reportType,
			invite_id: reportType === "awarded" ? selectedInviteId : undefined,
			design_schema: effectiveSchema,
		}),
		[reportType, selectedInviteId, effectiveSchema]
	);

	const { preview, isLoading, isFetching } = useReportPreview(
		rfxId,
		evalId,
		params
	);
	const exportReport = useExportReport(rfxId, evalId);

	// AC6 — panel is only available once at least one supplier is awarded.
	if (!hasAwards) {
		return (
			<div
				data-testid="report-panel-disabled"
				className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500"
			>
				Reports become available once at least one supplier has been awarded.
			</div>
		);
	}

	const awardedMissingSupplier =
		reportType === "awarded" && selectedInviteId == null;

	const handleExport = () => {
		const payload: ReportExportPayload = {
			type: reportType,
			invite_id: reportType === "awarded" ? selectedInviteId : undefined,
			design_schema: effectiveSchema,
		};
		exportReport.mutate(payload);
	};

	const renderPreview = () => {
		if (awardedMissingSupplier) {
			return (
				<div className="text-sm text-gray-500 py-8 text-center" data-testid="report-select-supplier">
					Select an awarded supplier to preview their report.
				</div>
			);
		}
		if (isLoading || (isFetching && !preview)) {
			return (
				<div className="py-8" data-testid="report-preview-loading">
					<LoadingSpinner label="Loading preview..." verticalPadding={4} />
				</div>
			);
		}
		if (!preview) {
			return null;
		}
		return (
			<div className="flex flex-col gap-4" data-testid="report-preview">
				{preview.cover_html && (
					<PreviewPageCard pageLabel="Cover Page" previewWidth={PREVIEW_WIDTH}>
						<ScaledIframe
							srcDoc={preview.cover_html}
							title="Cover page preview"
							nativeWidth={A4_PORTRAIT.width}
							previewWidth={PREVIEW_WIDTH}
							minHeight={A4_PORTRAIT.height}
						/>
					</PreviewPageCard>
				)}
				{preview.pages.map((page, index) => {
					const dims =
						page.orientation === "landscape" ? A4_LANDSCAPE : A4_PORTRAIT;
					return (
						<PreviewPageCard
							key={page.label || index}
							pageLabel={page.label || `Page ${index + 1}`}
							previewWidth={PREVIEW_WIDTH}
						>
							{preview.header_html && (
								<ScaledIframe
									srcDoc={preview.header_html}
									title={`Header ${index + 1}`}
									nativeWidth={dims.width}
									previewWidth={PREVIEW_WIDTH}
									fixedHeight={40}
								/>
							)}
							<ScaledIframe
								srcDoc={page.html}
								title={`${page.label || `Page ${index + 1}`} preview`}
								nativeWidth={dims.width}
								previewWidth={PREVIEW_WIDTH}
								minHeight={dims.height}
							/>
							{preview.footer_html && (
								<ScaledIframe
									srcDoc={preview.footer_html}
									title={`Footer ${index + 1}`}
									nativeWidth={dims.width}
									previewWidth={PREVIEW_WIDTH}
									fixedHeight={36}
								/>
							)}
						</PreviewPageCard>
					);
				})}
			</div>
		);
	};

	return (
		<div data-testid="report-panel" className="space-y-4">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div className="space-y-3">
					<ToggleButtonGroup
						options={REPORT_TYPE_OPTIONS}
						value={reportType}
						onChange={(value) => setReportType(value as ReportType)}
					/>
					{reportType === "awarded" && (
						<div className="w-72" data-testid="report-supplier-selector">
							<SelectWithLabel
								label="Awarded supplier"
								options={supplierOptions}
								value={
									supplierOptions.find((o) => o.value === selectedInviteId) ??
									null
								}
								onChange={(option) =>
									setSelectedInviteId(
										option && !Array.isArray(option)
											? (option.value as number)
											: undefined
									)
								}
							/>
						</div>
					)}
				</div>

				<Button
					type="button"
					btntype="primary"
					isLoading={exportReport.isPending}
					disabled={awardedMissingSupplier || exportReport.isPending}
					disabledStyle={awardedMissingSupplier}
					onClick={handleExport}
					data-testid="report-export-btn"
				>
					Export report
				</Button>
			</div>

			{exportReport.isError && (
				<div
					data-testid="report-export-error"
					className="flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-3"
				>
					<span className="text-sm text-red-700">
						The report failed to export. Please try again.
					</span>
					<Button
						type="button"
						btntype="secondary"
						size="sm"
						onClick={handleExport}
						data-testid="report-export-retry"
					>
						Retry
					</Button>
				</div>
			)}

			<div className="bg-gray-100 rounded-lg p-4 overflow-x-auto">
				{renderPreview()}
			</div>
		</div>
	);
};
